// src/components/personalize/PersonalizationSurveyOverview.tsx
// ============================================================
// REFACTOR: Data Intake Surveys → Context Modules
// UX framing to keep users grounded in EA journey context
// ============================================================

import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertCircle, Sparkles } from 'lucide-react';
import SurveyService, { isSurveyAnswerAnswered } from '../../services/surveyService';

interface PersonalizationSurveyOverviewProps {
  clientId: string;
  userId: string;
  onBackToOverview?: () => void;
  onOpenSurvey?: (surveyType: 'strategy' | 'capabilities' | 'readiness') => void;
}

export function PersonalizationSurveyOverview({
  clientId,
  userId,
  onBackToOverview,
  onOpenSurvey
}: PersonalizationSurveyOverviewProps) {
  // Survey progress state
  const [strategyProgressCount, setStrategyProgressCount] = useState(0);
  const [capabilitiesProgressCount, setCapabilitiesProgressCount] = useState(0);
  const [readinessProgressCount, setReadinessProgressCount] = useState(0);

  const [strategicPrioritiesCompletionStatus, setStrategicPrioritiesCompletionStatus] = useState<'incomplete' | 'pending' | 'complete'>('incomplete');
  const [dataAnalyticsCompletionStatus, setDataAnalyticsCompletionStatus] = useState<'incomplete' | 'pending' | 'complete'>('incomplete');
  const [dataReadinessCompletionStatus, setDataReadinessCompletionStatus] = useState<'incomplete' | 'pending' | 'complete'>('incomplete');

  const [strategicPrioritiesResponses, setStrategicPrioritiesResponses] = useState<any>(null);
  const [dataAnalyticsResponses, setDataAnalyticsResponses] = useState<any>(null);
  const [dataReadinessResponses, setDataReadinessResponses] = useState<any>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Helper functions from legacy HomePage
  const toAnswered = (responses: any) => {
    if (!responses) return 0;
    if (Array.isArray(responses)) {
      return responses.filter((r: any) => {
        const value = r?.response ?? r?.answer ?? r?.value;
        return isSurveyAnswerAnswered(value);
      }).length;
    }
    if (typeof responses === 'object') {
      return Object.values(responses).filter((value) => isSurveyAnswerAnswered(value)).length;
    }
    return 0;
  };

  const toTotal = (responses: any) => {
    if (!responses) return 0;
    return Array.isArray(responses) ? responses.length : 1;
  };

  const getStatus = (_completionStatus: string, answered: number, total: number): 'not-started' | 'in-progress' | 'complete' => {
    if (total > 0 && answered >= total) return 'complete';
    if (answered <= 0) return 'not-started';
    return 'in-progress';
  };

  const getStrategicPrioritiesProgress = () => ({
    answered: toAnswered(strategicPrioritiesResponses),
    total: toTotal(strategicPrioritiesResponses),
    status: getStatus(strategicPrioritiesCompletionStatus, toAnswered(strategicPrioritiesResponses), toTotal(strategicPrioritiesResponses))
  });

  const getDataAnalyticsProgress = () => ({
    answered: toAnswered(dataAnalyticsResponses),
    total: toTotal(dataAnalyticsResponses),
    status: getStatus(dataAnalyticsCompletionStatus, toAnswered(dataAnalyticsResponses), toTotal(dataAnalyticsResponses))
  });

  const getDataReadinessProgress = () => ({
    answered: toAnswered(dataReadinessResponses),
    total: toTotal(dataReadinessResponses),
    status: getStatus(dataReadinessCompletionStatus, toAnswered(dataReadinessResponses), toTotal(dataReadinessResponses))
  });

  // Load survey data
  const loadSurveyData = async () => {
    if (!clientId) return;

    try {
      setIsLoading(true);
      setLoadError(null);

      const result = await SurveyService.getAllProgress(clientId, true);

      if (result.strategy) {
        setStrategicPrioritiesResponses(result.strategy.responses || null);
        setStrategicPrioritiesCompletionStatus(result.strategy.completionStatus || 'incomplete');
        setStrategyProgressCount(toAnswered(result.strategy.responses));
      }

      if (result.capabilities) {
        setDataAnalyticsResponses(result.capabilities.responses || null);
        setDataAnalyticsCompletionStatus(result.capabilities.completionStatus || 'incomplete');
        setCapabilitiesProgressCount(toAnswered(result.capabilities.responses));
      }

      if (result.readiness) {
        setDataReadinessResponses(result.readiness.responses || null);
        setDataReadinessCompletionStatus(result.readiness.completionStatus || 'incomplete');
        setReadinessProgressCount(toAnswered(result.readiness.responses));
      }

    } catch (error) {
      console.error('Error loading survey data:', error);
      setLoadError('Failed to load module progress. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSurveyData();
  }, [clientId]);

  // Module handlers (renamed from survey)
  const handleOpenModule = (surveyType: 'strategy' | 'capabilities' | 'readiness') => {
    if (onOpenSurvey) {
      onOpenSurvey(surveyType);
    }
  };

  // Module card renderer (renamed from survey card)
  const renderModuleCard = (
    title: string,
    description: string,
    surveyType: 'strategy' | 'capabilities' | 'readiness',
    progress: { answered: number; total: number; status: 'not-started' | 'in-progress' | 'complete' }
  ) => {
    const getStatusBadge = () => {
      switch (progress.status) {
        case 'complete':
          return (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 whitespace-nowrap flex-shrink-0">
              <CheckCircle className="w-3 h-3" />
              <span className="text-[11px] font-semibold">Applied</span>
            </div>
          );
        case 'in-progress':
          return (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap flex-shrink-0">
              <Clock className="w-3 h-3" />
              <span className="text-[11px] font-semibold">In Progress</span>
            </div>
          );
        case 'not-started':
          return (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-600 whitespace-nowrap flex-shrink-0">
              <AlertCircle className="w-3 h-3" />
              <span className="text-[11px] font-semibold">Not Started</span>
            </div>
          );
      }
    };

    const getCtaLabel = () => {
      switch (progress.status) {
        case 'complete':
          return 'Edit Module';
        case 'in-progress':
          return 'Continue';
        case 'not-started':
          return 'Begin';
      }
    };

    const getProgressPercent = () => {
      if (progress.total === 0) return 0;
      return Math.round((progress.answered / progress.total) * 100);
    };

    const getProgressText = () => {
      if (progress.status === 'complete') {
        return `${progress.answered}/${progress.total} Applied`;
      }
      return `${progress.answered} / ${progress.total} inputs`;
    };

    const getProgressTextColor = () => {
      return progress.status === 'complete' ? 'text-emerald-600' : 'text-dvaNavy';
    };

    return (
      <div className="rounded-2xl ring-1 ring-[#D9DFEA] bg-white p-8 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150">
        {/* Header with title and badge */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-lg font-bold text-dvaNavy leading-tight flex-1 min-w-0">{title}</h3>
          {getStatusBadge()}
        </div>

        {/* Description */}
        <p className="text-sm text-dvaTextSecondary mb-6 leading-relaxed">
          {description}
        </p>

        {/* Progress section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-dvaTextSecondary font-medium">Progress</span>
            <span className={`text-sm font-bold ${getProgressTextColor()}`}>
              {getProgressText()}
            </span>
          </div>
          
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${
                progress.status === 'complete' ? 'bg-emerald-500' : 'bg-dvaBlueGraySoft'
              }`}
              style={{ width: `${getProgressPercent()}%` }}
            />
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={() => handleOpenModule(surveyType)}
          className="w-full inline-flex items-center justify-center rounded-xl bg-dvaNavy px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#0f2642] transition-all"
        >
          {getCtaLabel()}
        </button>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dvaOrange mx-auto mb-3"></div>
          <p className="text-sm text-dvaTextSecondary">Loading module progress...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-sm text-dvaTextSecondary">{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ============================================================
          REFACTOR A: New header with "Context Modules" framing
          - Title: "Context Modules" (not "Data Intake Surveys")
          - Subtitle explaining value
          - Microcopy about immediate application
          ============================================================ */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-dvaNavy">Context Modules</h2>
            <Sparkles className="w-5 h-5 text-dvaOrange" />
          </div>
          <p className="text-sm text-dvaTextSecondary">
            Each module adds signal to how opportunities are evaluated and ranked.
          </p>
        </div>
        {/* Microcopy - right aligned */}
        <div className="text-xs text-dvaTextSecondary/70 italic whitespace-nowrap pt-1">
          Changes apply immediately to results.
        </div>
      </div>

      {/* Module cards grid (renamed from survey cards) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {renderModuleCard(
          'Strategic Priorities',
          'Define organizational strategy, innovation posture, and investment priorities to weight opportunities accordingly.',
          'strategy',
          getStrategicPrioritiesProgress()
        )}

        {renderModuleCard(
          'Data & Analytics Capabilities',
          'Assess current data infrastructure and analytics maturity to calibrate feasibility scoring.',
          'capabilities',
          getDataAnalyticsProgress()
        )}

        {renderModuleCard(
          'Data Readiness & Governance',
          'Evaluate governance frameworks and organizational readiness to refine implementation estimates.',
          'readiness',
          getDataReadinessProgress()
        )}
      </div>
    </div>
  );
}